/**
 * Een concept hernoemen vanaf een canvas.
 *
 * Staat hier apart omdat er twee canvassen zijn die het doen — het gewone
 * React-Flow-canvas via `ConceptNode`, en het banen-canvas dat z'n eigen
 * render-pad heeft — en omdat hernoemen meer is dan de naam wegschrijven:
 * de andere taal wordt bijgewerkt en er volgt een dubbelcheck. Twee kopieën
 * daarvan lopen vroeg of laat uiteen.
 *
 * De taal die de modelleur op dat moment ziet is de bron; de tegenhanger
 * wordt vertaald, maar alleen wanneer die sinds het begin van de bewerking
 * onaangeroerd is gebleven — anders overschrijf je wat iemand anders (of de
 * modelleur zelf in het attributenpaneel) net heeft ingetypt.
 */
import { ApiError, api } from "../../api/client";
import { useWorkspaceData } from "../../state/workspace";

export async function hernoemConcept(opts: {
  workspaceId: string;
  branchId: string;
  conceptId: string;
  langKey: "nl" | "en";
  nieuweNaam: string;
}): Promise<void> {
  const { workspaceId, branchId, conceptId, langKey } = opts;
  const store = useWorkspaceData.getState().store;
  const concept = store.getConcept(conceptId);
  if (!concept) return;
  const next = opts.nieuweNaam.trim();
  if (next === (concept.name[langKey] ?? "").trim()) return;
  const otherLang: "nl" | "en" = langKey === "nl" ? "en" : "nl";
  const counterpartBefore = concept.name[otherLang] ?? "";
  await useWorkspaceData.getState().patchConcept(workspaceId, branchId, conceptId, {
    name: { ...concept.name, [langKey]: next },
  });
  if (!next) return;

  // Vertalen-bij-blur: dezelfde laatste-bewerking-wint-regel als
  // `BilingualField` in het attributenpaneel — de zojuist bewerkte taal is de
  // nieuwe bron van waarheid en de tegenhanger gaat mee.
  try {
    const res = await api.translate({
      source_lang: langKey,
      target_lang: otherLang,
      text: next,
      kind: "name",
    });
    const live = useWorkspaceData.getState().store.getConcept(conceptId);
    if (live && (live.name[otherLang] ?? "") === counterpartBefore) {
      await useWorkspaceData.getState().patchConcept(workspaceId, branchId, conceptId, {
        name: { ...live.name, [otherLang]: res.text },
      });
    }
  } catch (err) {
    // 412 = geen aanmelding bij Anthropic ingesteld; spiegel de brontekst zodat beide
    // talen gevuld blijven. Andere fouten laten we staan.
    if (err instanceof ApiError && err.status === 412) {
      const live = useWorkspaceData.getState().store.getConcept(conceptId);
      if (live && (live.name[otherLang] ?? "") === counterpartBefore) {
        await useWorkspaceData.getState().patchConcept(workspaceId, branchId, conceptId, {
          name: { ...live.name, [otherLang]: next },
        });
      }
    }
  }

  // Dubbelcheck op het moment dat de naam definitief is: is dit object
  // daarmee identiek geworden (type + naam + stereotypes) aan een bestaand,
  // dan volgt de samenvoeg-vraag. Bij een lege naam gebeurt er niets.
  await useWorkspaceData.getState().checkConceptDuplicate(workspaceId, branchId, conceptId);
}
